import { useLayoutEffect, useMemo, useRef } from "react";
import { offsetShadow, reconcilePieces, TYPE, type Palette, type PlacedPiece } from "@chess/shared";
import { PieceSvg } from "@/chess/board/pieces";

export interface SquareCoord {
  row: number;
  col: number;
}

export type BoardPosition = (string | null)[][];

interface Props {
  palette: Palette;
  size?: number;
  showCoords?: boolean;
  selected?: SquareCoord | null;
  position: BoardPosition;
  onSquareClick?: (row: number, col: number, piece: string | null) => void;
  candidateSquares?: SquareCoord[];
  mistakeFrom?: SquareCoord | null;
  mistakeTo?: SquareCoord | null;
  // Subtle highlight of the move that led to the shown position (no badge).
  lastMoveFrom?: SquareCoord | null;
  lastMoveTo?: SquareCoord | null;
  // Draw an arrow from→to (e.g. the bad move on a training drill).
  arrowFrom?: SquareCoord | null;
  arrowTo?: SquareCoord | null;
  arrowColor?: string;
  // Draw several arrows at once (e.g. the candidate moves of a choice drill).
  // Rendered in addition to the single arrow above.
  arrows?: { from: SquareCoord; to: SquareCoord; color: string }[];
  // Render from Black's perspective (rank 1 at top).
  flipped?: boolean;
  // Slide-animation duration (ms) when pieces move between positions. 0 = off.
  animMs?: number;
}

// Stable piece ids carry across position changes so React keeps the same DOM
// node and the move animates. The matching algorithm lives in @chess/shared
// (reconcilePieces) so the web and React Native boards share one implementation.

export function ChessBoard({
  palette,
  size = 480,
  showCoords = true,
  selected = null,
  position,
  onSquareClick,
  candidateSquares = [],
  mistakeFrom = null,
  mistakeTo = null,
  lastMoveFrom = null,
  lastMoveTo = null,
  arrowFrom = null,
  arrowTo = null,
  arrowColor,
  arrows = [],
  flipped = false,
  animMs = 180,
}: Props) {
  const p = palette;
  // The board uses border-box sizing with a 3px border, so the squares the CSS
  // grid actually lays out span the *content* box (size − 2×border), not size.
  // The absolute piece/arrow layers must use the same square size or they drift.
  const BORDER = 3;
  const inner = size - BORDER * 2;
  const sq = inner / 8;
  const pieceSize = sq * 0.84;

  // Stable piece ids across renders so moves animate. Reconcile the incoming
  // board against the last placement on every render (cheap; 32 pieces max).
  const placedRef = useRef<PlacedPiece[]>([]);
  const idRef = useRef(0);
  const placed = useMemo(() => {
    const next = reconcilePieces(placedRef.current, position, () => ++idRef.current);
    placedRef.current = next;
    return next;
    // Re-run whenever the board contents change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position]);

  // DOM nodes per piece id + the on-screen pixel each was last placed at, so we
  // can drive the slide imperatively with the Web Animations API. CSS
  // transitions proved unreliable here (React commits the new transform with no
  // intervening paint, so the transition never runs); .animate() always runs.
  const nodeRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const lastXY = useRef<Map<number, { x: number; y: number }>>(new Map());
  useLayoutEffect(() => {
    // Figure out which placed pieces changed square since the last commit.
    const target = new Map<number, { x: number; y: number }>();
    const moved: number[] = [];
    for (const pc of placed) {
      const d = disp({ row: pc.row, col: pc.col });
      const x = d.col * sq;
      const y = d.row * sq;
      target.set(pc.id, { x, y });
      const prev = lastXY.current.get(pc.id);
      if (prev && (prev.x !== x || prev.y !== y)) moved.push(pc.id);
    }
    // Only animate a real move (≤4 pieces shift — covers castling). A bigger
    // jump (first data load, position scrub, board flip, resize) snaps instead,
    // so pieces never "fly in from the centre" at the start.
    const animate = animMs > 0 && moved.length > 0 && moved.length <= 4;
    if (animate) {
      for (const id of moved) {
        const node = nodeRefs.current.get(id);
        const prev = lastXY.current.get(id);
        const to = target.get(id);
        if (!node || !prev || !to) continue;
        node.animate(
          [
            { transform: `translate(${prev.x}px, ${prev.y}px)` },
            { transform: `translate(${to.x}px, ${to.y}px)` },
          ],
          { duration: animMs, easing: "ease", fill: "none" },
        );
      }
    }
    // Commit the new positions as the baseline for next time.
    lastXY.current = target;
    const live = new Set(placed.map((pc) => pc.id));
    for (const id of [...nodeRefs.current.keys()]) if (!live.has(id)) nodeRefs.current.delete(id);
  });
  // Map a true board coord (row 0 = rank 8, col 0 = file a) to its on-screen
  // square index, accounting for board flip (Black's perspective).
  const disp = (c: SquareCoord) => (flipped ? { row: 7 - c.row, col: 7 - c.col } : c);
  // Center of a true-coord square in board pixels.
  const center = (c: SquareCoord) => {
    const d = disp(c);
    return { x: (d.col + 0.5) * sq, y: (d.row + 0.5) * sq };
  };
  // Display order of cells: 0..63 rendered, mapped back to true coords.
  const cells: { ri: number; ci: number }[] = [];
  for (let dr = 0; dr < 8; dr++) {
    for (let dc = 0; dc < 8; dc++) {
      cells.push(flipped ? { ri: 7 - dr, ci: 7 - dc } : { ri: dr, ci: dc });
    }
  }

  return (
    <div
      style={{
        width: size,
        height: size,
        display: "grid",
        gridTemplateColumns: "repeat(8, 1fr)",
        gridTemplateRows: "repeat(8, 1fr)",
        border: `3px solid ${p.border}`,
        boxShadow: offsetShadow(p, 8, 8),
        position: "relative",
        fontFamily: '"Segoe UI Symbol","Apple Symbols","Noto Sans Symbols",sans-serif',
        userSelect: "none",
      }}
    >
      {/* Square backgrounds + highlights, drawn UNDER the arrow. The
          interactive buttons above are transparent and only carry pieces,
          coords and badges — so the arrow sits between background and figures. */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "grid",
          gridTemplateColumns: "repeat(8, 1fr)",
          gridTemplateRows: "repeat(8, 1fr)",
          zIndex: 0,
          pointerEvents: "none",
        }}
      >
        {cells.map(({ ri, ci }) => {
          const isLight = (ri + ci) % 2 === 0;
          const isMistakeFrom = mistakeFrom && ri === mistakeFrom.row && ci === mistakeFrom.col;
          const isMistakeTo = mistakeTo && ri === mistakeTo.row && ci === mistakeTo.col;
          const isLastFrom = lastMoveFrom && ri === lastMoveFrom.row && ci === lastMoveFrom.col;
          const isLastTo = lastMoveTo && ri === lastMoveTo.row && ci === lastMoveTo.col;
          const isSelected = selected && selected.row === ri && selected.col === ci;
          const isCandidate = candidateSquares.some((s) => s.row === ri && s.col === ci);
          let bg = isLight ? p.boardLight : p.boardDark;
          if (isLastFrom || isLastTo) bg = p.accent;
          if (isMistakeFrom) bg = p.mistakeFrom;
          if (isMistakeTo) bg = p.mistakeTo;
          if (isCandidate) bg = p.moveCandidate;
          if (isSelected) bg = p.selected;
          return <div key={`bg-${ri}-${ci}`} style={{ background: bg }} />;
        })}
      </div>
      {(() => {
        // Collect the single arrow + any multi-arrows into one list so they
        // share the same overlay (and the same geometry math).
        const allArrows: { from: SquareCoord; to: SquareCoord; color: string }[] = [];
        if (arrowFrom && arrowTo) {
          allArrows.push({ from: arrowFrom, to: arrowTo, color: arrowColor ?? p.danger });
        }
        for (const ar of arrows) allArrows.push(ar);
        if (allArrows.length === 0) return null;
        return (
          <svg
            width={inner}
            height={inner}
            viewBox={`0 0 ${inner} ${inner}`}
            style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none", overflow: "visible", zIndex: 1 }}
            aria-hidden
          >
            {allArrows.map((ar, idx) => {
              const a = center(ar.from);
              const b = center(ar.to);
              const col = ar.color;
              const dx = b.x - a.x;
              const dy = b.y - a.y;
              const len = Math.hypot(dx, dy) || 1;
              const ux = dx / len;
              const uy = dy / len;
              // Stop the shaft short of the target so the arrowhead sits on it.
              const head = sq * 0.34;
              const ex = b.x - ux * head;
              const ey = b.y - uy * head;
              const w = Math.max(3, sq * 0.1);
              // Arrowhead triangle.
              const px = -uy;
              const py = ux;
              const hw = sq * 0.2;
              const p1 = `${b.x - ux * head + px * hw},${b.y - uy * head + py * hw}`;
              const p2 = `${b.x - ux * head - px * hw},${b.y - uy * head - py * hw}`;
              const p3 = `${b.x},${b.y}`;
              return (
                <g key={idx} opacity={0.55}>
                  <line
                    x1={a.x}
                    y1={a.y}
                    x2={ex}
                    y2={ey}
                    stroke={col}
                    strokeWidth={w}
                    strokeLinecap="round"
                  />
                  <polygon points={`${p1} ${p2} ${p3}`} fill={col} />
                </g>
              );
            })}
          </svg>
        );
      })()}
      {cells.map(({ ri, ci }) => {
          const piece = position[ri]?.[ci] ?? null;
          const isLight = (ri + ci) % 2 === 0;
          const isMistakeTo = mistakeTo && ri === mistakeTo.row && ci === mistakeTo.col;
          const isSelected = selected && selected.row === ri && selected.col === ci;
          // Display-edge detection for coordinate labels (depends on flip).
          const onLeftEdge = flipped ? ci === 7 : ci === 0;
          const onBottomEdge = flipped ? ri === 0 : ri === 7;

          // Backgrounds/highlights are painted by the layer behind the arrow;
          // these buttons are transparent and sit above the arrow (zIndex 2).
          return (
            <button
              key={`${ri}-${ci}`}
              onClick={() => onSquareClick?.(ri, ci, piece)}
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                margin: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                zIndex: 2,
                cursor: onSquareClick ? "pointer" : "default",
                outline: isSelected ? `3px solid ${p.fg}` : "none",
                outlineOffset: -3,
                fontFamily: "inherit",
              }}
            >
              {showCoords && onLeftEdge && (
                <span
                  style={{
                    position: "absolute",
                    left: 4,
                    top: 3,
                    fontSize: Math.max(9, sq * 0.16),
                    fontWeight: 700,
                    color: isLight ? p.fgMuted : p.id === "vice" ? "#ffd5e8" : "rgba(255,255,255,0.85)",
                    fontFamily: TYPE.mono,
                    lineHeight: 1,
                  }}
                >
                  {8 - ri}
                </span>
              )}
              {showCoords && onBottomEdge && (
                <span
                  style={{
                    position: "absolute",
                    right: 4,
                    bottom: 3,
                    fontSize: Math.max(9, sq * 0.16),
                    fontWeight: 700,
                    color: isLight ? p.fgMuted : p.id === "vice" ? "#ffd5e8" : "rgba(255,255,255,0.85)",
                    fontFamily: TYPE.mono,
                    lineHeight: 1,
                  }}
                >
                  {"abcdefgh"[ci]}
                </span>
              )}
              {isMistakeTo && (
                <span
                  style={{
                    position: "absolute",
                    top: 3,
                    right: 3,
                    fontSize: Math.max(10, sq * 0.18),
                    fontWeight: 800,
                    color: p.dangerFg,
                    fontFamily: TYPE.mono,
                    background: p.fg,
                    padding: "1px 5px",
                    border: `2px solid ${p.fg}`,
                    lineHeight: 1,
                    pointerEvents: "none",
                    zIndex: 3,
                  }}
                >
                  !
                </span>
              )}
            </button>
          );
        })}
      {/* Animated piece layer: each piece is absolutely positioned at its
          (flip-aware) square. Slides are driven imperatively via the Web
          Animations API in the layout effect above (CSS transitions don't fire
          reliably for these React-committed transform changes). */}
      <div style={{ position: "absolute", inset: 0, zIndex: 2, pointerEvents: "none" }}>
        {placed.map((pc) => {
          const d = disp({ row: pc.row, col: pc.col });
          return (
            <div
              key={pc.id}
              ref={(el) => {
                if (el) nodeRefs.current.set(pc.id, el);
                else nodeRefs.current.delete(pc.id);
              }}
              style={{
                position: "absolute",
                width: sq,
                height: sq,
                left: 0,
                top: 0,
                transform: `translate(${d.col * sq}px, ${d.row * sq}px)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 0,
              }}
            >
              <PieceSvg piece={pc.piece} size={pieceSize} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
